import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WORLD_CUP_WELCOME } from '../../../src/worldcup/worldcup.constants';
import {
  WORLD_CUP_WELCOME_ANIM,
  WORLD_CUP_WELCOME_PHASE_LAST,
  WORLD_CUP_WELCOME_SCROLL_CONTENT_TOP_EXTRA,
} from './WorldCupWelcomeScreen.constants';
import { getWorldCupWelcomeScreenStyles } from './WorldCupWelcomeScreen.styles';
import { WelcomeSplashPhase } from './WelcomeSplashPhase';
import { WelcomeRulesPhase } from './WelcomeRulesPhase';
import { WelcomePointsPhase } from './WelcomePointsPhase';

export interface WorldCupWelcomeScreenProps {
  onEnter: () => void;
  testID?: string;
}

export const WorldCupWelcomeScreen: React.FC<WorldCupWelcomeScreenProps> = ({
  onEnter,
  testID = 'worldcup-welcome-screen',
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const overlayOpacity = useRef(new Animated.Value(1)).current;
  /**
   * True crossfade: incoming phase mounts first at full opacity (so its stagger entrance can
   * run from frame 1) while the outgoing phase is rendered as an absolute overlay on top and
   * fades from 1→0. This eliminates the JS-mount gap that was making the previous slide look
   * "stuck" on Android before the next one appeared.
   */
  const outgoingOpacity = useRef(new Animated.Value(0)).current;

  const [phase, setPhase] = useState(0);
  const [outgoingPhase, setOutgoingPhase] = useState<number | null>(null);
  const [isExiting, setIsExiting] = useState(false);
  const [isPhaseAnimating, setIsPhaseAnimating] = useState(false);

  const scrollContentPaddingTop = insets.top + WORLD_CUP_WELCOME_SCROLL_CONTENT_TOP_EXTRA;
  const styles = useMemo(
    () =>
      getWorldCupWelcomeScreenStyles(theme, {
        bottomInset: insets.bottom,
        topInset: insets.top,
        rightInset: insets.right,
      }),
    [theme, insets.bottom, insets.right, insets.top],
  );

  /** Web: stop scroll chaining to the document (Brave/black gaps + shell shifting). */
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverscroll = html.style.overscrollBehaviorY;
    const prevBodyOverscroll = body.style.overscrollBehaviorY;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehaviorY = 'none';
    body.style.overscrollBehaviorY = 'none';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overscrollBehaviorY = prevHtmlOverscroll;
      body.style.overscrollBehaviorY = prevBodyOverscroll;
    };
  }, []);

  const runOverlayExit = useCallback(
    (onDone: () => void) => {
      Animated.timing(overlayOpacity, {
        toValue: 0,
        duration: WORLD_CUP_WELCOME_ANIM.OVERLAY_EXIT_MS,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: Platform.OS !== 'web',
      }).start(({ finished }) => {
        if (finished) onDone();
      });
    },
    [overlayOpacity],
  );

  const handleEnterApp = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);
    runOverlayExit(onEnter);
  }, [isExiting, onEnter, runOverlayExit]);

  const crossfadeToPhase = useCallback(
    (nextPhase: number) => {
      if (isPhaseAnimating || isExiting) return;
      setIsPhaseAnimating(true);
      const useNativeDriver = Platform.OS !== 'web';

      /**
       * Mount the next phase as the active body and snapshot the current phase as a fading
       * overlay on top. Both are in the tree by the time we kick off the fade, so the next
       * phase's stagger entrance is already running underneath as the overlay melts away —
       * no JS-mount gap, no "previous slide" pause on Android.
       */
      const fromPhase = phase;
      setOutgoingPhase(fromPhase);
      setPhase(nextPhase);
      outgoingOpacity.setValue(1);

      const startFade = () => {
        Animated.timing(outgoingOpacity, {
          toValue: 0,
          duration:
            WORLD_CUP_WELCOME_ANIM.PHASE_SLIDE_OUT_MS +
            WORLD_CUP_WELCOME_ANIM.PHASE_SLIDE_IN_MS,
          easing: Easing.out(Easing.cubic),
          useNativeDriver,
        }).start(({ finished }) => {
          if (finished) setOutgoingPhase(null);
          setIsPhaseAnimating(false);
        });
      };

      /** Wait one frame so the next phase commits + lays out before we start fading the overlay. */
      requestAnimationFrame(startFade);
    },
    [isExiting, isPhaseAnimating, outgoingOpacity, phase],
  );

  const handleNext = useCallback(() => {
    if (phase >= WORLD_CUP_WELCOME_PHASE_LAST) {
      handleEnterApp();
      return;
    }
    crossfadeToPhase(phase + 1);
  }, [crossfadeToPhase, handleEnterApp, phase]);

  const renderPhase = (phaseToRender: number) => {
    if (phaseToRender === 0) {
      return (
        <WelcomeSplashPhase
          isExiting={isExiting}
          onNext={handleNext}
          testID={`${testID}-splash`}
        />
      );
    }
    if (phaseToRender === 1) {
      return (
        <WelcomeRulesPhase
          phase={phaseToRender}
          isExiting={isExiting}
          scrollContentPaddingTop={scrollContentPaddingTop}
          onNext={handleNext}
          testID={`${testID}-rules`}
        />
      );
    }
    return (
      <WelcomePointsPhase
        phase={phaseToRender}
        isExiting={isExiting}
        scrollContentPaddingTop={scrollContentPaddingTop}
        onNext={handleNext}
        testID={`${testID}-points`}
      />
    );
  };

  return (
    <Animated.View
      style={[styles.root, { opacity: overlayOpacity }]}
      pointerEvents="auto"
      testID={testID}
    >
      <View style={styles.phaseAnimated}>{renderPhase(phase)}</View>
      {outgoingPhase !== null && outgoingPhase !== phase ? (
        <Animated.View
          style={[StyleSheet.absoluteFillObject, { opacity: outgoingOpacity }]}
          pointerEvents="none"
        >
          {renderPhase(outgoingPhase)}
        </Animated.View>
      ) : null}
      <View style={styles.skipWrap} pointerEvents="box-none">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={WORLD_CUP_WELCOME.SKIP}
          disabled={isExiting}
          hitSlop={12}
          onPress={handleEnterApp}
          style={({ pressed, hovered }) => [
            styles.skipPressable,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && !isExiting && styles.skipPressablePressed,
            hovered && Platform.OS === 'web' && styles.skipPressableHovered,
          ]}
          testID={`${testID}-skip`}
        >
          <Text style={styles.skipLabel}>{WORLD_CUP_WELCOME.SKIP}</Text>
        </Pressable>
      </View>
    </Animated.View>
  );
};
